/**
 * In-memory ring of the last console errors/warnings and uncaught errors.
 * Technique adapted from RedLine src/overlay.js:23-40. Idempotent: state lives
 * on globalThis so HMR re-evaluation or a second install never double-patches.
 */
import type { ConsoleEntry, ConsoleLevel } from './types';

const CAP = 60;
const MAX_MESSAGE = 700;
const STATE_KEY = Symbol.for('ic.feedback.consoleRing');

interface RingState {
  entries: ConsoleEntry[];
  installed: boolean;
  recording: boolean;
  originals: { error: typeof console.error; warn: typeof console.warn } | null;
  teardown: (() => void) | null;
}

function state(): RingState {
  const g = globalThis as unknown as Record<symbol, RingState | undefined>;
  let s = g[STATE_KEY];
  if (!s) {
    s = { entries: [], installed: false, recording: false, originals: null, teardown: null };
    g[STATE_KEY] = s;
  }
  return s;
}

function stringify(arg: unknown): string {
  try {
    if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
    if (typeof arg === 'string') return arg;
    if (arg !== null && typeof arg === 'object') return JSON.stringify(arg);
    return String(arg);
  } catch {
    try {
      return String(arg);
    } catch {
      return '[unserializable]';
    }
  }
}

function currentRoute(): string {
  try {
    return globalThis.location.pathname.slice(0, 200);
  } catch {
    return '';
  }
}

function push(level: ConsoleLevel, parts: unknown[]): void {
  const s = state();
  if (s.recording) return;
  s.recording = true;
  try {
    const message = parts.map(stringify).join(' ').slice(0, MAX_MESSAGE);
    s.entries.push({ level, message, route: currentRoute(), ts: Date.now() });
    if (s.entries.length > CAP) s.entries.splice(0, s.entries.length - CAP);
  } catch {
    // Recording must never break the page.
  } finally {
    s.recording = false;
  }
}

export function installConsoleRing(): void {
  const s = state();
  if (s.installed) return;
  s.installed = true;
  const originals = { error: console.error, warn: console.warn };
  s.originals = originals;
  console.error = (...args: unknown[]) => {
    push('error', args);
    originals.error.apply(console, args);
  };
  console.warn = (...args: unknown[]) => {
    push('warn', args);
    originals.warn.apply(console, args);
  };
  const onError = (e: ErrorEvent): void => push('uncaught', [e.error ?? e.message]);
  const onRejection = (e: PromiseRejectionEvent): void => push('unhandledrejection', [e.reason]);
  globalThis.addEventListener('error', onError);
  globalThis.addEventListener('unhandledrejection', onRejection);
  s.teardown = () => {
    globalThis.removeEventListener('error', onError);
    globalThis.removeEventListener('unhandledrejection', onRejection);
    console.error = originals.error;
    console.warn = originals.warn;
  };
}

/** Undo install (tests / dev tooling). */
export function uninstallConsoleRing(): void {
  const s = state();
  s.teardown?.();
  s.teardown = null;
  s.installed = false;
  s.entries.length = 0;
}

export function getConsoleLog(): ConsoleEntry[] {
  return state().entries.map((e) => ({ ...e }));
}

export function clearConsoleLog(): void {
  state().entries.length = 0;
}
